/* =====================================================================
   E7 — THE WATER, THE EARTH AT THE FRONT, THE BEYOND, THE SKY

   Everything in this file is shader work over the ONE field the engine
   already has (ROOM-PLAN §7): `buildWater`, `buildBlock` and `buildBeyond`
   in relief.js are three CPU meshes rebuilt with the view — an nA x 56
   water sheet with a ten-step apron, a cut face walked off the drape's
   last column, and a graded conjecture grid whose seam vertices are
   copied one by one. Here they are four draws of two constant grids.

   What is drawn, in order (the engine calls each hook by name):
     1. the sky wash, behind the horizon and inside the window only
     2. the ground (E1/E2, unchanged except the two hooks marked E7)
     3. the beyond — the conjecture, mirrored out, sinking to the table,
        hazed, ending where the wash has taken it
     4. the water — one plane at the datum over the same field, banded by
        depth, aproned at every limit so no edge is ever ruled
     5. the earth at the front — the cut face, strata struck in its own
        plane, the water standing in the cut

   The constants are relief.js's own, cited at each one.
   ===================================================================== */
(function (root) {
'use strict';

var Q = new URLSearchParams(location.search);
if (Q.get('e7') === '0') return;

/* ---- relief.js's constants, by line ------------------------------- */
var WATER_OPACITY = 0.46;     // relief.js:159
var WATER_Z       = -0.4;     // relief.js:4232 — in bed*EXAG units
var HAZE_START = 0.19, HAZE_FULL = 0.77, HAZE_MAX = 0.95;   // :229-231
var HAZE_TILT  = 45 * Math.PI / 180;                        // :232
var SKY_NEAR = 0.62, SKY_SPAN = 0.91;                       // :237-238
var SKY_FROM = 30, SKY_TO = 58;                             // :255, degrees
var BEYOND_REACH = 2.2;   // :240 — plate-heights of conjecture, every way out
var BEYOND_FADE = 0.28;       // :241 — the outer part of the reach that fades
var BEYOND_FLATTEN = 0.34;    // :246 — how much of the mirrored ground survives
/* R1 (a): plate-heights over which the flatten is eased in, so the
   conjecture leaves the shore at the ground's OWN height and the north and
   south rows are met instead of cut. ?flatrun= to pair it. */
var FLAT_RUN = +(Q.get('flatrun') || 0.34);
var BEYOND_SINK = 1.0 + 1.0 / 6.0;  // plate-heights the conjecture takes to reach the table
var BEYOND_HATCH = 9.0;       // :249 — sheet units between the rules of the conjecture
var FORE_HOLD = 0.15, FORE_OUT = 0.45;   // :4247-4248 — the sea's apron
var DEG = Math.PI / 180;

/* the two depth bands, as `bed` (relief.js DEPTHS, :2365; the engine's own
   numbers, already in the ground shader) */
var DEP1 = -0.47979658, DEP2 = -1.17666850;

/* THE AIM-LIFT, MEASURED (item 5). relief.js's 0.34 of the highest ground is
   an ORTHOGRAPHIC figure; under the perspective camera the same term left the
   raised picture low (E1 measured ~25 px at tilt 55). Ladder against 8102 at
   #970,431,2120,55,0, paired per column over the 398 columns both resolve:
   0 -> -19 px, 0.35 -> -8, 0.7 -> +3, 1.0 -> +13; the zero crossing is 0.594.
   ?lift=1 is relief.js's own number, for the pair. */
var LIFT_K = +(Q.get('lift') || 0.6);

/* R3 (a): `?winds=0` draws Terra Incognita as the bare paper it was, for
   the pair. */
var RHUMBS = Q.get('winds') !== '0';

/* R16 asked the sea's beyond to give up its wash over the land's own two
   runs, and `?seabeyond=0` put R10's for-ever wash back for the pair. R20
   builds the whole beyond the other way up — the record's own material at
   the edge row, crossfaded to the conjecture's paper over those same two
   runs — so there is no second wash left to switch back to and the switch
   is gone with it. R16's result is kept: past the runs the beyond is one
   paper tone whether the shore it left was sea or land.
   The pair for R20 is the branch's own base, captured before the edit. */

/* R19: ?surf=1 — the three surfaces flat, so a seam can be counted rather
   than squinted at. engine.js carries the note. */
var SURF = Q.get('surf') === '1';
/* R61 (c): R39's `?seahaze` is DELETED. Tommy, twice, on the grey the
   water plane took from the distance wash: "it mostly just appears as a
   bug or as a way to make rendering cheaper, it doesn't make it look more
   impressive", and then "drop it". R41 (d) had already set the default to
   0, so the whole term below was multiplied by zero on every frame — a
   switch, a uniform, a varying and six lines of fragment arithmetic that
   could not change a pixel. The plane does not go into the distance wash
   at all now: the GROUND still does (engine.js uHazeCol, untouched), and
   the sea over it keeps its own colour however far back it lies. */

/* R31's rule, kept and made structural (R35): the material's run in the
   two OPEN directions is the ACROSS overshoot alone, so the sea has no
   west end and the unsurveyed paper no east end. ?beyopen is gone with the
   drawing it switched; the ground fragment has the rule now. */


/* WD2 (2026-09-21) offered `?vwind=sea` here: a downwind GRAIN on the
   open sea's plane, soft streaks 72 map units apart growing with the wind
   to 8/255 of contrast at 30 km/h (screens repo room-e9/wd2-0921). Tommy:
   "Not sure it's really clear. The thing is, people aren't going to view
   this on different days, so they're not really going to experience
   different wind conditions." Deleted; the corner arrow (voyage.js) is
   the wind's one mark. */

var W = { on: true, LIFT_K: LIFT_K };
root.RoomWater = W;

/* =================================================================
   (1) THE SEA, AS THE SHEET DRAWS IT — the waterlining

   survey-map.js:2384-2422. Eight lines of equal depth in three flights:
   three crowding under the coast, two outside the shoal's edge, three
   closing on the floor of the deep, at five weights (.sv-waterline--0..4,
   styles.css:840-845). The last five carry `lod-mid`, so they come in
   with the engine's own mid generation (uLev.x) rather than at a
   threshold.
   ================================================================= */
W.FS_SEA = `
uniform vec4 uWL0, uWL1;      // the eight levels, in v
uniform vec4 uWLW0, uWLW1;    // their widths (device px at ratio 1)
uniform vec4 uWLA0, uWLA1;    // their opacities
vec3 waterLining(vec3 g, float v, float grad, float dpr, float mid){
  vec4 L = uWL0, Wd = uWLW0, Al = uWLA0;
  for (int p = 0; p < 2 + uZero; p++){
    for (int i = 0; i < 4 + uZero; i++){
      float lv = L[i]; if (lv <= 0.0) continue;
      float w = (p == 0 && i < 3) ? 1.0 : mid;     // lod-mid on the last five
      if (w <= 0.002) continue;
      float d = abs(v - lv)/max(grad, 1e-7);
      g = mix(g, SEALN, inkOf(Wd[i]*dpr, d)*Al[i]*w);
    }
    L = uWL1; Wd = uWLW1; Al = uWLA1;
  }
  return g;
}`;

/* R20's W.FS_RECORD — the sheet's own fills lifted out of the ground
   fragment so the second drawing could wear the first's material — is
   DELETED (R35). There is one drawing and one fragment; a copy of the tint
   stack is a second opinion waiting to drift out of step. */

/* (6) THE SMOOTHER RECONSTRUCTION (E2's open item 1) — the Catmull-Rom
   read of the field — is engine.js FIELD_GLSL's `fieldAt` since JG1, so the
   mesh, the ink and everything standing on the ground read ONE surface. The
   B-spline and the `?bic` pair went with it. */


/* =================================================================
   (R3 a) TERRA INCOGNITA — THE WINDS OVER THE PAPER NOBODY HAS WALKED

   Tommy, review 1: "terra incognita seems to have lost its cool
   pattern/effect". On 8102 the unsurveyed ground east of the front is not
   bare paper: it carries the WINDS. survey-map.js:4294 draws thirty-two
   rhumbs from the point the compass rose stands on at the fit, clipped to
   the blank ground, in the water's own ink at the two faintest weights the
   plate carries (.sv-rhumb .6 at .24, .sv-rhumb--main .8 at .34, every
   fourth line, both non-scaling-stroke). Its own note: "A portolan drew no
   coast it had not been given, but it drew the WINDS ... which is exactly
   the state of the ground east of the front — paper, and a bearing."

   The engine drew `col = PAPER` there and stopped, which is the plain beige
   plain he is looking at. Here the winds are an ARITHMETIC ON THE POINT and
   not 32 clipped paths: the angle to the centre, rounded to the nearest of
   thirty-two, and the perpendicular distance to that ray measured in DEVICE
   pixels through the fragment's own Jacobian — so the ink is the same
   weight at every zoom, tilt and bearing, and a non-scaling-stroke is what
   it is for nothing. No texture, no geometry, no bytes.

   The centre is fixed on the GROUND, not on the window, exactly as 8102
   fixes it (its judgment: "A centre that moved with the window would have
   the winds swimming over the paper, which no printed chart does"): the
   ground the rose covers at the fit, 71 + 30/z in from the east neat line
   and 71 + 26/z down from the north one, z = 1 at the fit.
   ================================================================= */
W.FS_TERRA = `
uniform vec4 uRhumb;     // centre x, y (MAP units), reach, on
/* R10 (b): how far past the record's east end the winds are still drawn.
   Zero (the uniform's own default) in the GROUND, where 8102 clips them to
   the blank ground; in the BEYOND it is the run over which the conjecture
   takes up its paper, so the winds die with the paper they are drawn on
   instead of stopping on a boundary that carries no line. */
/* R31: TWO runs now. The FIRST is how far past the EAST neat line the
   winds are drawn — 0 in the ground (8102 clips them to the blank ground),
   no limit in the BEYOND, because east of the front the paper has none and
   the winds are drawn on that paper. The SECOND is the same allowance
   ACROSS, where the material does end: they die over the conjecture's sink
   instead of cutting on the mould's own 26-unit skirt. */
uniform vec2 uRhumbOut;
const vec3 RHUMB = vec3(0.29019608,0.43921569,0.47450980);   // #4a7079 --sea-line
/* the blank ground: east of the front (its fade included), inside the
   strip and its skirt, west of the east neat line */
bool terraAt(vec2 s){
  if (uRhumb.w < 0.5) return false;
  if (uFrontK.x > 1.5) return false;                 // nothing is unsurveyed
  if (s.x > uFrontK.y + uRhumbOut.x) return false;   // past the east neat line
  if (s.y < uMould.z - 26.0 - uRhumbOut.y || s.y > uMould.w + 26.0 + uRhumbOut.y) return false;
  if (uFrontK.x < 0.5) return true;                  // no front: all of it is blank
  return s.x >= feTerraFrom(s);   /* FE1 (engine.js): the days-shaped front */
}
vec3 rhumbs(vec3 col, vec2 s){
  if (!terraAt(s)) return col;
  vec2 d = s - uRhumb.xy;
  float r = length(d);
  if (r > uRhumb.z) return col;
  const float STEP = 6.28318530718/32.0;
  float a = atan(d.y, d.x);
  float n = floor(a/STEP + 0.5);
  float th = n*STEP;
  vec2 perp = vec2(-sin(th), cos(th));
  float sd = dot(d, perp);                    // map units off the ray
  /* the fragment's own map -> device px Jacobian, the ground shader's own
     expression: vMap is linear across a triangle, so this is exact under
     any tilt, turn or perspective */
  mat2 J = mat2(dFdx(s), dFdy(s));
  float det = J[0].x*J[1].y - J[0].y*J[1].x;
  mat2 Sx = mat2(J[1].y, -J[0].y, -J[1].x, J[0].x)*(1.0/(abs(det) > 1e-12 ? det : 1e-12));
  float dpx = abs(sd) * length(Sx*perp);
  bool main32 = abs(fract(n*0.25)) < 0.001;
  float w = (main32 ? 0.8 : 0.6) * uDpr;
  float al = main32 ? 0.34 : 0.24;
  return mix(col, RHUMB, inkOf(w, dpx)*al);
}`;

/* ==== R35: THE CONJECTURE'S OWN NUMBERS, FOR THE ONE PROGRAM THAT DRAWS
   IT NOW. E7 cut the ground at the plate (`beyondPlate`) and let a SECOND
   drawing have everything outside; there is one drawing, so the cut is
   gone and what is left of the beyond is these six constants and the
   material they shape. engine.js's GROUND_FS writes them in. ==== */
W.BEY = { FLAT_RUN: FLAT_RUN, FLATTEN: BEYOND_FLATTEN, SINK: BEYOND_SINK,
          REACH: BEYOND_REACH, FADE: BEYOND_FADE, HATCH: BEYOND_HATCH };

/* =================================================================
   THE DISTANCE, AND THE WASH BEHIND IT

   relief.js:5846-5866. A point g ground-units past the focus is g·sin t
   deeper than the camera's focus plane, and every figure is quoted in
   PLATE-HEIGHTS of ground, so the arithmetic is the same at every tilt
   and every zoom. CAM_D cancels out of both ends of every ramp, which is
   why none of it appears here.
   ================================================================= */
/* R10 (b): the two halves are exported on their own as well. depthOut
   wants the rigid body's uniforms (uView, uYaw, uAtt) and so can only go
   in a VERTEX shader that already has XFORM; the two RAMPS want nothing
   but uDist, and the ground's FRAGMENT shader now needs them — see
   `W.GLSL_DEPTH` / `W.GLSL_RAMPS` and engine.js's GROUND_VS/GROUND_FS. */
var DEPTH_GLSL = `
float depthOut(vec2 m, float h){
  vec2 d = (m - (uView.xy + uView.zw*0.5));
  float ry = d.x*uYaw.y + d.y*uYaw.x;
  return -(ry*uAtt.y + h*uAtt.x);          // map units, away from the eye
}`;
var RAMPS_GLSL = `
uniform vec4 uDist;    // plateAcross, reach, haze, sinT
uniform vec3 uHazeCol;
float skyOf(float dep){
  float st = uDist.x * uDist.w * uDist.y;
  return clamp((dep - ${SKY_NEAR}*st) / max(1e-4, ${SKY_SPAN}*st), 0.0, 1.0);
}
float hazeOf(float dep){
  float n = ${HAZE_START} * uDist.x * uDist.w;
  float f = ${HAZE_FULL} * uDist.x * uDist.w * uDist.y;
  return clamp((dep - n)/max(1e-4, f - n), 0.0, 1.0) * uDist.z;
}`;
/* R61: DIST_GLSL (RAMPS + DEPTH) had one reader, the water plane's vertex
   shader, and it only ever fed the distance wash (c) deletes. The two
   chunks are still exported for engine.js's ground, which is the drawing
   that goes into the wash. */
W.GLSL_DEPTH = DEPTH_GLSL; W.GLSL_RAMPS = RAMPS_GLSL;


/* THE FILM — ONE VARNISH OVER THE WHOLE WINDOW. On 8102 this is a DOM
   layer (.canvas-plot::after) lying over the plate's canvas, so it takes
   the sky wash, the conjecture and the water exactly as it takes the
   ground. E1 put it in the ground shader alone; without it here the sky
   at the top of the window came back 12/255 paler than 8102's and the
   conjecture read a shade too clean. Same expression, same figures. */
var FILM_GLSL = `
uniform float uFilm;
vec3 filmOf(vec3 col, vec2 sh){
  if (uFilm <= 0.0) return col;
  vec2 q = (sh - uPlate.xy) / uPlate.zw;
  float r = length(vec2((q.x-0.5)/1.25, (q.y-0.44)/1.55));
  vec4 rad;
  if (r < 0.34) rad = vec4(1.0,1.0,1.0,0.0);
  else if (r < 0.66) rad = mix(vec4(1.0,1.0,1.0,0.0), vec4(0.494,0.361,0.180,0.16), (r-0.34)/0.32);
  else if (r < 0.86) rad = mix(vec4(0.494,0.361,0.180,0.16), vec4(0.408,0.290,0.141,0.34), (r-0.66)/0.20);
  else if (r < 1.0)  rad = mix(vec4(0.408,0.290,0.141,0.34), vec4(0.329,0.227,0.102,0.52), (r-0.86)/0.14);
  else rad = vec4(0.329,0.227,0.102,0.52);
  float t = clamp(q.y, 0.0, 1.0);
  vec4 lin = mix(vec4(0.776,0.612,0.361,0.20), vec4(0.698,0.525,0.290,0.26), t);
  float a = rad.a + lin.a*(1.0-rad.a);
  vec3 c = a > 0.0 ? (rad.rgb*rad.a + lin.rgb*lin.a*(1.0-rad.a))/a : vec3(1.0);
  float f = a * uFilm;
  return col * (1.0 - f) + col * c * f;
}`;

/* =================================================================
   (4) THE SKY WASH — behind the horizon, and inside the window only

   Tommy, 2026-09-10, reversing my own "there is no sky, it is a model on
   a desk": "the actual rectangle within the paper as a kind of magic
   portal into a world ... the distance wouldn't necessarily be sky ...
   almost like a kind of watercolour continuation." So not a sky box: the
   wash a Victorian bird's-eye view lays behind its drawn ground, and
   NEVER a saturated blue.

   It was `.sky-wash` in styles.css, on a `<div>` relief.js made under the
   canvas (R12 removed those rules; survey/v32 has them). Here it is a
   quad clipped to the window's own scissor: the same three stops, the
   same rain hatch, the same `--sky-k` (the tilt alone, nought on the flat
   sheet) and the same `--sky-hz` (relief.js:5903 — where the paper gives
   out, never lower than the 32% the plate always used).
   ================================================================= */
/* A QUAD ON THE PAPER AT THE WINDOW'S OWN RECT, not a screen-space one.
   The sheet never tilts — only the MAP is the rigid body that stands up —
   so the window is the same undistorted rectangle at every tilt, which is
   what makes 8102's DOM layer legal in the first place. Drawn here it is
   the same pixels, it is right when the camera leaves the desk (the reader standing),
   and it takes the film with everything else. */
/* ==== DR3, 2026-09-21: AND THE WASH IS ON THE PAPER TOO ==============
   Tommy: "the map and axis labels go straight on but the paper bends up
   to the right." The ruler was the loud half of that; this quad was the
   quiet one. Every other pass that draws inside the window goes through
   engine.js's sheetToClip, which adds objects.js's `paperSurf` — the
   ground, the sea, the marks, the names all ride the shoulder the journal
   puts under the sheet. This one wrote a bare -0.002 and stayed on the
   PLANE the paper lies on, so at a tilt the wash behind the horizon sat a
   little proud of the ground drawn in front of it.

   The same arithmetic, then: the paper's face, and the two thousandths
   below it this pass has always carried to keep the wash under the
   ground.

   WHAT THIS IS NOT. It is a FOUR-VERTEX quad (S.vaoQuad, one triangle
   strip), so the surface is sampled at the window's four corners and read
   bilinearly between them — the shoulder's own curve is not in it. That
   is right enough for a wash whose whole job is to be behind everything,
   and it is what the corners of the window are actually standing at; a
   tessellated quad would be a second mesh to keep in step with the
   ground's, which is the trade E7 took everywhere else in this file. ==== */
var SKY_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a;
out vec2 vUv; out vec2 vSh;
uniform vec4 uPlate; uniform vec3 uSheet; uniform mat4 uVP;
` + ((window.RoomObjects && RoomObjects.paperInkGLSL)
     ? RoomObjects.paperInkGLSL()
     /* the stub engine.js:310 keeps, for a harness with no desk under it */
     : 'float paperSurf(vec2 p){ return 0.0; }\n') + `
void main(){
  vec2 p = uPlate.xy + a*uPlate.zw;
  vUv = a; vSh = p;
  gl_Position = uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), (paperSurf(p) - 0.002)*uSheet.z, 1.0);
}`;
var SKY_FS = `#version 300 es
precision highp float;
in vec2 vUv; in vec2 vSh;
out vec4 o;
uniform vec4 uPlate;
uniform vec3 uTop, uHor, uHaz;
uniform vec3 uKHzRain;  // sky-k, sky-hz, rain
__FILM__
void main(){
  float t = clamp(vUv.y, 0.0, 1.0);
  float hz = max(0.02, uKHzRain.y);
  vec3 c = t < hz*0.47 ? mix(uTop, uHor, t/(hz*0.47))
         : (t < hz ? mix(uHor, uHaz, (t - hz*0.47)/max(1e-4, hz*0.53)) : uHaz);
  /* the rain is a wash too, never a sprite: one diagonal hatch at 74deg */
  float r = uKHzRain.z;
  if (r > 0.001){
    float d = dot(gl_FragCoord.xy, vec2(cos(radians(74.0)), sin(radians(74.0))));
    float f = fract(d/7.0)*7.0;
    c = mix(c, vec3(0.910,0.933,0.941), 0.10*r*(1.0 - smoothstep(0.6, 1.4, f)));
  }
  o = vec4(filmOf(c, vSh), uKHzRain.x);
}`;

/* =================================================================
   (3) THE BEYOND — DELETED (R35)

   relief.js built a graded CPU mesh out to BEYOND_EDGE plate-heights and
   mirrored the nearest surveyed ground across every shore; E7 made that
   two draws of two constant grids; R20 rebuilt it the other way up, with
   the record's own material at the edge row crossfading outward. All of it
   was a SECOND DRAWING of the same country, and a second drawing of one
   edge can only ever be biased against the first, never made equal to it.
   The GROUND draws it now — its own mesh, its own field (field.js's
   margin), its own material (engine.js GROUND_FS's R35 block). What
   survives here is the six constants W.BEY hands the ground, and the water
   plane below, which is ONE plane over the record and the margin alike.
   ================================================================= */

/* =================================================================
   (2a) THE WATER PLANE AT TILT

   relief.js buildWater (:4253-4515). One plane at WATER_Z, banded by the
   bed under it, translucent, cut to the water alone, and aproned at every
   limit it has — the two shores, the west end and the block's east end —
   so it never ends on a ruled line. All of that is the same field and
   four smoothsteps; the nA x 56 mesh with its ten-step apron is gone.
   ================================================================= */
var WAT_VS = `#version 300 es
precision highp float; precision highp sampler2D;
layout(location = 0) in vec2 a;
out vec2 vMap; out float vWet; out vec2 vSh;
uniform vec4 uGrid; uniform float uRelief; uniform vec4 uPlateM;
/* the water's along limits: west, block east. R41 (c): only .y is read.
   The WEST limit is carried for the record and deliberately not applied —
   there is no west clip, no apron and no FORE_OUT term on that side, so at
   the surveyed start the plane does exactly what it does in open sea
   inside the record: the field's own level ends it and nothing else. */
uniform vec2 uLimA;
__FIELD____FRONT____XFORM__
void main(){
  vec2 m = uGrid.xy + a * uGrid.zw;
  /* R61: vBed is gone. R20 moved the band test into the fragment and
     nothing has read this varying since — but it was still costing a
     vertex texture fetch and, since R46, a three-octave noise per vertex
     of the plane's grid. Deleted, with vDep, which only the distance
     wash R61 (c) removes ever read. */
  float h = ${WATER_Z} * uRelief;
  /* THE WATER IS CLIPPED TO THE RECORD, exactly as the ground is (E7b).

     relief.js carries the sheet a FORE_OUT apron (.45 of a plate-height)
     past every shore so the sea never ends on a ruled line — and there the
     apron lies over the sheet's own printed paper. Here the paper past the
     record is CONJECTURE, and a wash of surveyed sea laid over conjecture
     paints unsurveyed ground blue: measured at tilt 55, lobes of the
     coastline extruded a quarter of the window north of the plate and a
     broad blue field over the north-west (room-e3x/merged2-t55.png). The
     ruling cuts both ways — nothing surveyed hidden, and nothing unsurveyed
     painted as if it were survey.

     So the plane stops at the record's own rect, over eight map units so it
     is not a ruled line either, and WHAT CARRIES THE SEA ON past the shore
     is the conjecture's own sea (BEY_FS's vSea), which is what it is for.
     The apron's other job — no hard edge — is done by the two meeting at
     the same shore at the same level. */
  /* ==== R35: ONE PLANE, OVER RECORD AND MARGIN ALIKE. The clip above was
     the whole of R20 OPEN 2 and R28 OPEN 1: the record's sea is a tint
     PLUS this translucent plane and the conjecture's was the tint alone,
     so the two seas met on a hard vertical step, and between the record's
     BED at a wet neat line and the conjecture's flat plane a sea-depth
     higher there was a gap only a WALL could stand in (R19's curtain — "a
     curtain over a deep wet seam is a WALL, and at some attitudes it is a
     big flat one"). There is one sea now. The plane is not clipped to
     anything across or west: the field's own level ends it (the fragment's
     smoothstep on the bed), the margin eases land to the datum so it ends
     on its own shore, and west of January 2022 the bed stays at the
     record's own western depth, so the sea goes on as far as the pan does.
     What still cuts it is the FRONT, because the front is a cut and water
     carried over it would stand a block-depth in the air (:4330). ==== */
  float acr = uPlateM.w;
  float wet = smoothstep(0.0, ${FORE_OUT}*acr, uLimA.y - m.x);
  vWet = wet;
  vMap = m;
  vec3 pw = mapToSheet(m, h); vSh = pw.xy;
  gl_Position = sheetToClip(pw);
}`;
var WAT_FS = `#version 300 es
precision highp float; precision highp sampler2D;
in vec2 vMap; in float vWet; in vec2 vSh;
out vec4 o;
uniform float uOpac;
uniform vec4 uPlate;
__FIELD__
__FILM__
/* ==== WL1, 2026-10-09: AND THE SEA TAKES THE LANTERN TOO. R48/R52 put the
   lamp's pool on the ground, the marks and the names; this plane was left
   cold, so at night under a tilt (where it stands over the bed and is what
   is seen) the water did not fall into the dark outside the pool as the
   paper round it did. The fold's print is lit by the sheet's own lampTemp,
   so the moment the corner was taken the water came down 8% at
   #1182,431,2120,14,2 (Tommy: "the colouring/shadowing/shading change[s]
   slightly still when I grab the corner"). Same five lines, same pool in
   sheet units; uLampN is nought by day. */
uniform vec4 uLampS;
uniform float uLampN;
vec3 lampTemp(vec3 c, vec2 wp){
  if (uLampN <= 0.0) return c;
  vec2 d = (wp - uLampS.xy)/uLampS.zw;
  float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
  vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
  float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
  return c*mix(vec3(1.0), t*lit, uLampN);
}
float smst(float a, float b, float v){ float t = clamp((v-a)/(b-a),0.0,1.0); return t*t*(3.0-2.0*t); }
void main(){
  /* ==== R20: THE PLANE'S THREE TONES ARE THE FIELD'S, NOT THE MESH'S.
     R10 (a) found this in the BEYOND and it was here as well: vBed is the
     grid's own vertex value, linear across each triangle, and a hard test
     on a linearly interpolated varying is a POLYGON.
     Under a tilt the record's own water came back terraced along the
     grid's facets — the angular steps in the sea north of the December
     line at #89,452,1018,64,479, which is half of what Tommy means by
     "blocky at surface level like ground". The bed is sampled HERE now
     and each band edge is the ground's own one-device-pixel rule
     (engine.js:445), so every edge in the water is the field's curve. One
     fetch, on water fragments only. ==== */
  /* ==== R61 (b): AND THE PLANE DOES NOT BAND ITSELF ON THE OPEN SEA'S
     FLOOR. THIS IS THE DOUBLING.

     Tommy, 2026-09-14, on ?west=bed: "the pattern is a little nauseating
     because of the way it's doubled". It is not a symmetry in the noise —
     the seabed has no axis and never had one (the field was sampled for
     one about every reflection and transposition through the lattice
     origin and came back at the shuffled baseline, R61 report §b). It is
     the SAME ISOLINE DRAWN TWICE, at two heights.

     The ground tints itself at bed -1.17666850 (engine.js:890) and draws
     that band where the GROUND is — 23.5 map units below the datum. The
     plane tinted itself at the same level (DEP2 below) from the bed
     VERTICALLY BELOW ITS OWN FRAGMENT, and the plane is 8 map units below
     the datum. Two copies of one curve, 15.5 map units apart in HEIGHT,
     which a tilt lays out as 15.5 x tan(tilt) map units apart ACROSS the
     sheet — 21 map units at his 53 degrees, about 36 device px at his
     zoom, whatever the bed is doing. Over the record that offset is
     invisible, because the only place the record's bed crosses DEP2 is a
     steep coastal slope and the two copies fall within a line's width of
     each other. Over the OPEN SEA the bank is a wide gentle dome, so the
     two copies stand 36 px apart and each bank reads as a pair of lobes.

     So the plane is banded by the RECORD's floor alone, and the open sea's
     bank is the GROUND's own tint seen through the water — drawn once,
     where the ground actually is. The alternative (marching the plane's
     lookup down the view ray until it meets the bed, so the two copies
     coincide) costs a second three-octave evaluation per water fragment
     and buys a band that is merely darker; this costs one LESS. ==== */
  float bed = riseOf(texture(uField, uvOf(vMap)).r);
  /* A SOFT EDGE, NOT A CUT. relief.js drops a whole quad where the
     deepest of its four corners is above -0.02, so the water's own
     boundary is a polygon of its mesh; here it is the field's own level. */
  float wet = vWet * smoothstep(-0.01, -0.045, bed);
  if (wet < 0.004) discard;
  /* shoal, shallow, deep — relief.js WATER_BANDS :160 */
  float grad = max(length(vec2(dFdx(bed), dFdy(bed))), 1e-7);
  vec3 c = vec3(0.63922,0.72549,0.74118);
  c = mix(c, vec3(0.55686,0.66275,0.69804), clamp((${DEP1} - bed)/grad + 0.5, 0.0, 1.0));
  c = mix(c, vec3(0.43529,0.58431,0.63529), clamp((${DEP2} - bed)/grad + 0.5, 0.0, 1.0));
  /* R61 (c): and the plane does NOT go into the distance wash. R39 put
     the sea into it with the ground in front of it and R41 (d) turned the
     term off; this is the term, gone. */
  o = vec4(lampTemp(filmOf(c, vSh), vSh), uOpac*wet);
${SURF?'  o = vec4(0.0,0.85,0.9,1.0);':''}
}`;

/* =================================================================
   (2b) THE EARTH AT THE FRONT — DELETED (R35)

   E7 drew the survey front as a SECOND OBJECT: a constant grid of its own,
   its own program, standing FACE_OFF 0.35 map units proud of the cut so it
   would not z-fight the one-cell RAMP the mould's 0.8-unit step left under
   it (R28 OPEN 2) — and R28 then had to cull its back faces, because from
   inside the ground there was a wall where no wall is (Tommy: "you can
   still see through the beyond to the main terrain's underside"). R19's
   CURTAIN was the same program again, on a third grid, hanging between two
   meshes that resolved one edge at two pitches.

   Tommy, 2026-09-14: "i'm open on how the cliff should work, we could even
   question the concept if it makes it easier? it does have to drop from
   the top to terra incognita, it could be a similar edge to the sides or
   even slope down if we wanted?"

   So the front is the SAME construction as the other three sides: the
   field eased to the paper datum, only over a SHORT run (field.js's
   SCARP, 24 map units — a fall of about seven to one at the 2026 front),
   which the ground's own mesh resolves as a steep face of its own
   triangles, drawn in the downs' own paper (CE1 took the brown bedding
   off it, 2026-10-08). One surface, one program, no proud offset, no cull, no
   curtain, and no corner to mitre (R19 OPEN 1 / R28 OPEN 4 cannot open,
   because a corner of one surface has no seam in it).
   ================================================================= */


/* =================================================================
   THE MODULE
   ================================================================= */
var G = null;          // { gl, S, prog, setCommon, EXAG }
var P = {};            // the programs
var sky = { top: [0.698,0.753,0.757], hor: [0.910,0.875,0.784], haz: [0.871,0.816,0.722],
            rain: 0, reach: 1 };
var skyTo = null, skyFrom = null, easeT0 = 0, EASE_MS = 700, easeGen = 0;

function hex2(h){
  var n = parseInt(h.replace('#',''), 16);
  return [((n>>16)&255)/255, ((n>>8)&255)/255, (n&255)/255];
}
function lerp3(a, b, t){ return [a[0]+(b[0]-a[0])*t, a[1]+(b[1]-a[1])*t, a[2]+(b[2]-a[2])*t]; }

function grid(gl, nx, ny) {
  var v = new Float32Array((nx+1)*(ny+1)*2), k = 0;
  for (var y = 0; y <= ny; y++) for (var x = 0; x <= nx; x++) { v[k++] = x/nx; v[k++] = y/ny; }
  var idx = new Uint32Array(nx*ny*6); k = 0;
  for (var y2 = 0; y2 < ny; y2++) for (var x2 = 0; x2 < nx; x2++) {
    var a0 = y2*(nx+1)+x2, b0 = a0+1, c0 = a0+nx+1, d0 = c0+1;
    idx[k++]=a0; idx[k++]=c0; idx[k++]=b0; idx[k++]=b0; idx[k++]=c0; idx[k++]=d0;
  }
  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
  var vao = gl.createVertexArray(); gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bindVertexArray(null);
  return { vao: vao, n: idx.length };
}

/* R4: THE PROGRAMS, ON THEIR OWN. Neither needs the record, so the engine
   issues the compiles right after the first frame and they run while the
   worker builds the field; init() asks for the same two by the same names
   and the engine memoises. R35 took the other two (the beyond's and the
   face's) with the drawings they were for. */
var K = null;           // the engine's helpers, from whichever call came first
/* a program that can be drawn with NOW. precompile() only issued the four;
   the poller collects them off the critical path, and a draw that comes
   first (the sky during the reveal of a tilted load) collects its own here
   rather than reading `.u` off a program the driver has not finished. */
function live(p) {
  if (!p) return null;
  if (!p.u && K && K.finish) K.finish(p);
  return p.u ? p : null;
}
/* LD4 (2026-10-02): THE SKY IS NOT IN THE FIRST PICTURE unless the page
   boots tilted far enough to see it, so its link is left out of the cold
   load's compile and issued in the idle slot after READY (engine.js
   `lateCompile`, which calls this again with `late`). A view that turns
   up into the sky before then builds it on the spot, as before. */
W.precompile = function (gl, S, k, late) {
  K = K || k;
  if (P.sky) return;
  var C = k.chunks;
  function fill(src) {
    return src.replace(/__FIELD__/g, C.FIELD).replace('__FRONT__', C.FRONT)
              .replace('__XFORM__', C.XFORM)
              .replace('__SEA__', W.FS_SEA).replace('__TERRA__', W.FS_TERRA)
              /* R26: the ladder, written in here rather than kept here */
              .replace('__TINT__', root.RoomField.TINT_GLSL('rTint', 'rBand'))
              .replace(/__NLEV__/g, String(root.RoomField.TINT_NLEV))
              .replace(/__FILM__/g, FILM_GLSL)
              .replace(/\$\{EXAG\}/g, String(k.EXAG));
  }
  if (late || !S || S.tilt / DEG > SKY_FROM - 5) P.sky = k.prog(gl, SKY_VS, fill(SKY_FS), 'e7sky');
  if (!P.wat) P.wat = k.prog(gl, fill(WAT_VS), fill(WAT_FS), 'e7water');
  /* R35: and no grid but the sky's quad and the ground's own mesh. R19's
     768-column conjecture grid, R3 (b)'s 64 x 8 wall and R19's 1,024-column
     curtain strip are all gone with the drawings they carried. */
};

W.init = function (gl, S, k) {
  G = { gl: gl, S: S, prog: k.prog, setCommon: k.setCommon, EXAG: k.EXAG };
  W.precompile(gl, S, k);
  /* the waterlining's eight levels, once (survey-map.js:2404) */
  var f = S.fld, CO = f.COAST, L3 = CO + DEP1, L1 = CO + DEP2;
  W.wl = {
    lv: [CO*0.960, CO*0.905, CO*0.838, L3*0.945, L3*0.800, L1*0.880, L1*0.620, L1*0.330],
    cls: [0, 1, 2, 2, 3, 3, 4, 4]
  };
  var WD = [1.05, 0.85, 0.70, 0.58, 0.50], OP = [0.80, 0.58, 0.40, 0.26, 0.15];
  W.wl.w = W.wl.cls.map(function (c) { return WD[c]; });
  W.wl.a = W.wl.cls.map(function (c) { return OP[c]; });
  /* the sky as it stands NOW (the cache, or the default) — then listen.
     onChange fires immediately, and reading the same values twice is a
     no-op, so nothing eases at load. */
  readSky(true);
  if (root.Sky && Sky.onChange) Sky.onChange(onSky);
};

/* ---- the sky, and the ease (item 7) ------------------------------ */
function readSky(immediate) {
  if (!root.Sky || !Sky.wash) return;
  var w = Sky.wash(), wx = Sky.weather ? Sky.weather() : { rain: 0 };
  var next = { top: hex2(w.top), hor: hex2(w.horizon), haz: hex2(w.haze),
               rain: wx.rain || 0, reach: w.reach || 1 };
  if (immediate || !G) { sky = next; skyTo = null; return; }
  var same = Math.abs(next.haz[0]-sky.haz[0]) + Math.abs(next.haz[1]-sky.haz[1]) +
             Math.abs(next.haz[2]-sky.haz[2]) + Math.abs(next.rain - sky.rain);
  if (same < 0.002) { sky = next; skyTo = null; return; }
  /* THE SKY EASES, IT DOES NOT POP. The first picture is drawn with the
     last known sky (localStorage) or the default; when the weather lands
     the wash walks to it over EASE_MS instead of cutting. */
  skyFrom = { top: sky.top.slice(), hor: sky.hor.slice(), haz: sky.haz.slice(),
              rain: sky.rain, reach: sky.reach };
  skyTo = next; easeT0 = performance.now();
  W.easing = true;
  /* ONE EASE AT A TIME. Two sky changes inside EASE_MS left two rAF loops
     running against one pair of endpoints, and whichever finished first
     set skyTo to null under the other — which threw
     `Cannot read properties of null (reading 'top')` out of this line and
     stopped the frame. Found by driving Sky.setTime three times in a row
     for E8's captures; the generation token is the fix. */
  var gen = ++easeGen;
  (function step() {
    if (gen !== easeGen) return;
    var e = Math.min(1, (performance.now() - easeT0) / EASE_MS);
    var t = e < 0.5 ? 2*e*e : 1 - Math.pow(-2*e + 2, 2)/2;
    sky = { top: lerp3(skyFrom.top, skyTo.top, t), hor: lerp3(skyFrom.hor, skyTo.hor, t),
            haz: lerp3(skyFrom.haz, skyTo.haz, t),
            rain: skyFrom.rain + (skyTo.rain - skyFrom.rain)*t,
            reach: skyFrom.reach + (skyTo.reach - skyFrom.reach)*t };
    if (G) G.S.invalidate();
    if (e < 1) requestAnimationFrame(step); else { skyTo = null; W.easing = false; }
  })();
}
function onSky() { readSky(false); }

/* THE WEATHER IS NOT ON THE CRITICAL PATH (ROOM-PLAN §7). sky.js now
   reads its cache synchronously and holds the network call until this is
   called, after the first picture. */
W.afterFirstPicture = function () {
  if (root.Sky && Sky.begin) Sky.begin();
};

/* ---- what the engine asks for every frame ------------------------ */
/* WHERE THE RECORD IS, IN MAP UNITS — the rect the conjecture is cut on and
   the ground is cut at. NOT `plate`, which is the HOME YEAR's window: the
   record runs five years and `clampView` lets the view pan the whole of it
   (measured at #970,431,2120: plate.x 122 w 2120, front.west -8238 east
   2242, and the view's own x is -90). Cut at the plate, the ground DISCARDED
   itself over every column west of the home year and the conjecture drew
   there instead — 0.88 % of the window's surveyed tint at tilt 55, the depth
   bands and the coast off the west end of the record. It is the mould's own
   rect (survey-map.js bedAt: uMould = west, east, c0, c1). */
function recordRect(f) {
  return { x: f.front.west, y: f.front.c0,
           w: f.front.east - f.front.west, h: f.front.c1 - f.front.c0 };
}
function dist(S) {
  var f = S.fld;
  var PA = f.front.c1 - f.front.c0;                 // a plate-height, map units
  var sinT = Math.max(0.05, Math.sin(S.tilt));
  var haze = Math.min(S.tilt / HAZE_TILT, 1) * HAZE_MAX;
  return { PA: PA, sinT: sinT, haze: haze, reach: sky.reach };
}
/* R35: `beyondOn` and `cutOn` — how much of the conjecture was up, and
   whether the ground was cut at the plate — are gone with the two things
   they switched. The margin is the same surface at every attitude and at
   every pan, so there is nothing to fade in and nothing to cut. That also
   removes a real pop: below tilt 6 degrees, north-up, the ground used to
   paint blank PAPER outside the record's rect (no cut, no conjecture) and
   the conjecture took over as the tilt came on. ONE SURFACE cannot pop.
   `attitudeOn` and `outBy` went with them: nothing left asks how much
   conjecture is up, because all of it always is. */

/* ==== WC1: THE LIFTED STATIONS OF THE WINDOW, WHICH ARE NOT A PER-FRAME
   QUESTION. `RoomObjects.paperHeight` walks the whole book list for every
   station, and a 33 x 17 lattice of it came to 0.63-0.96 ms EVERY FRAME
   measured in the page (against 0.0006 ms for the four flat corners) —
   which is not a rounding error on a 16 ms budget, it is the frame.

   It does not belong in the frame at all. The paper's surface is a
   function of the sheet and the two books on the desk; the CAMERA is what
   changes sixty times a second, and the camera is not one of its
   arguments. So the heights are taken once and kept, as three numbers a
   station — the point in the world's css pixels and its height there —
   and the frame pays only the projection of them, which is the same eight
   multiplies the four corners above cost. Only the stations that are
   actually lifted are kept, because a flat one is inside the four
   corners' box already: at the rest layout that is a fifth of the
   lattice.

   WHAT INVALIDATES IT. The window's own rect and the sheet's layout, both
   in the key; and one live call of `paperHeight` at the south-east corner
   of the window, which is where the journal's drape is deepest — so a
   book that moved, or a drape that was rebuilt, rebuilds this too. One
   call a frame is 1.4 us. ==== */
var LIFT = null;
function liftPts(S, L, p) {
  var O = root.RoomObjects, ph = (O && O.paperHeight) ? O.paperHeight : null;
  if (!ph) return null;
  var key = p.l + ':' + p.t + ':' + p.w + ':' + p.h + ':' + L.ox + ':' + L.oy + ':' + L.k;
  var probe = ph(S, (p.l + p.w - L.ox) / L.k, (p.t + p.h - L.oy) / L.k);
  if (LIFT && LIFT.key === key && LIFT.probe === probe) return LIFT.a;
  var a = [], NX = 32, NY = 16;
  for (var j = 0; j <= NY; j++) {
    var PY = p.t + p.h * (j / NY), sy = (PY - L.oy) / L.k;
    for (var i = 0; i <= NX; i++) {
      var PX = p.l + p.w * (i / NX), sx = (PX - L.ox) / L.k;
      var Z = ph(S, sx, sy) * L.k;
      if (Z > 0) { a.push(PX, PY, Z); }
    }
  }
  LIFT = { key: key, probe: probe, a: a };
  return a;
}
/* the window's box in DEVICE pixels, from its four projected corners —
   right at the desk, and right when the camera has left it (the reader standing),
   where E1's css-space scissor was the wrong rectangle */
W.portalBox = function (S) {
  if (!S.VP || !S.layout) return null;
  var L = S.layout, p = S.portal, M = S.VP;
  var xs = [p.l, p.l + p.w], ys = [p.t, p.t + p.h];
  var x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
  for (var i = 0; i < 4; i++) {
    var X = xs[i & 1], Y = ys[i >> 1];
    var cx = M[0]*X + M[4]*(-Y) + M[12];
    var cy = M[1]*X + M[5]*(-Y) + M[13];
    var cw = M[3]*X + M[7]*(-Y) + M[15];
    if (Math.abs(cw) < 1e-6) cw = 1e-6;
    var nx = (cx/cw*0.5 + 0.5) * S.cv.width, ny = (cy/cw*0.5 + 0.5) * S.cv.height;
    if (nx < x0) x0 = nx; if (nx > x1) x1 = nx;
    if (ny < y0) y0 = ny; if (ny > y1) y1 = ny;
  }
  /* ==== WC1, 2026-09-21: AND THE FOUR CORNERS ARE ON THE PLANE.
     The four points above are the window at z = 0, which is where the
     window was until the paper got a drape over the two books (DR2).
     Everything drawn INTO the window rides that drape, and lifted paper
     projects toward the eye, so the box cut from the four FLAT corners is
     a few pixels short of the content on whichever side a book is under:
     the ground's last rows were scissored off before the stencil ever saw
     them. So the same rect is sampled on the paper's own surface —
     `RoomObjects.paperHeight` is the JS mirror of the GLSL `paperSurf`
     the engine's clip mesh is drawn with, so the two cannot part company
     — and the box is the union with the flat one, never smaller than it.

     A LATTICE AND NOT FOUR CORNERS, because the lifted window is a curved
     patch: its extreme on the glass can be a station in the middle of an
     edge, over the crown of a book, and not a corner at all. Nor are the
     edges enough on their own under perspective. It is a sample, so one
     device pixel of allowance is taken — and taken ONLY ON A SIDE THE
     DRAPE HAS ACTUALLY MOVED. A pad on all four was the obvious thing and
     it was wrong: the flat box's top edge lands a fraction inside the
     window's own north rule, so a pad there uncovered a row of the map
     along the whole north edge, where no book lifts anything and nothing
     about the picture should change. Measured: 14 blocks of the north
     edge different with the pad on all four sides, none with it on the
     moved sides only.

     Stations on flat paper are skipped: a planar rect projects to a
     convex quadrilateral and its interior points are inside the four
     corners' box already. ==== */
  var LP = liftPts(S, L, p);
  if (LP) {
    var f0 = x0, f1 = x1, g0 = y0, g1 = y1;         /* the flat box, kept */
    for (var i2 = 0; i2 < LP.length; i2 += 3) {
      var PX = LP[i2], PY = LP[i2 + 1], Z = LP[i2 + 2];
      var hx = M[0]*PX + M[4]*(-PY) + M[8]*Z + M[12];
      var hy = M[1]*PX + M[5]*(-PY) + M[9]*Z + M[13];
      var hw = M[3]*PX + M[7]*(-PY) + M[11]*Z + M[15];
      if (Math.abs(hw) < 1e-6) hw = 1e-6;
      var lx = (hx/hw*0.5 + 0.5) * S.cv.width, ly = (hy/hw*0.5 + 0.5) * S.cv.height;
      if (lx < x0) x0 = lx; if (lx > x1) x1 = lx;
      if (ly < y0) y0 = ly; if (ly > y1) y1 = ly;
    }
    if (x0 < f0) x0 -= 1;
    if (x1 > f1) x1 += 1;
    if (y0 < g0) y0 -= 1;
    if (y1 > g1) y1 += 1;
  }
  return { x: Math.floor(x0), y: Math.floor(y0),
           w: Math.ceil(x1 - x0), h: Math.ceil(y1 - y0) };
};

/* ==== R10 (c): WHAT THE DARK BAND ALONG THE TOP OF THE PORTAL IS.

   Measured at #1182,431,2120,80,30 (the tilt clamps to 75), a strip about
   six device pixels deep at the very top of the window reading (177,183,175)
   against (226,213,185) a few pixels below it. It is TWO things laid on one
   another, and neither of them is the haze:

     1. the SEA'S WASH at its thinnest. The beyond over the record's north
        shore was painted with the full banded sea tone right out to where
        the conjecture gives out, so the last visible rows of water were a
        cold blue-grey against warm hazed paper. R10 (a) above walks the
        banding back to one open-sea tone and thins the wash with it;
        measured on its own (?hz=ortho pinning the foot, so only (a) and (b)
        move) that alone takes the band from (177,183,175) to (206,199,179).
     2. the WASH'S OWN ZENITH STOP. The sky is three stops — zenith, horizon,
        haze — and it lightens downward exactly as the item says it should.
        But at tilt 75 relief.js's foot arithmetic (:5903) puts the horizon
        stop at 0.104 of the portal and the paper gives out at about 0.05, so
        the only sky the window ever shows is the top third of the ZENITH
        ramp: the darkest colour the sky has, laid straight against the paper,
        with the light horizon stop never reached.

   THE FOOT ITSELF IS LEFT ALONE, and that is a measured choice, not an
   omission. relief.js's figure is orthographic (a point N units out lands
   N.cos(t) down the window) while this engine's camera has perspective and
   an aim-lift, so the honest thing looked like projecting the point where
   the wash has finished taking the paper — a fixed (SKY_NEAR + SKY_SPAN) x
   plateAcross x reach map units out along the bearing at every tilt, since
   depthOut is linear in the rotated y — through the engine's own rigid body
   and VP. Built, and it puts the foot at 0.294 instead of 0.104 at tilt 75,
   because the aim-lift alone moves it 0.12 down: the picture got WORSE, the
   zenith ramp spreading over a fifth of the window instead of a twentieth
   (measured at y 200, (225,212,184) -> (198,197,180)). So the projection is
   kept as `?hz=proj` for the pair and the default is relief.js's own number.
   The band is fixed at the other end instead:

   THE ZENITH IS NOT IN A WINDOW THAT SHOWS NO SKY. The top stop is the
   colour of the sky straight up; when the foot sits at a twentieth of the
   portal the window holds a strip of sky that thin, all of it within a
   degree or two of the horizon, and none of it is zenith. So the top stop is
   walked from the horizon's colour to the zenith's over hz/0.32 — at the
   moderate tilts where the foot is at relief.js's own 0.32 cap it IS
   relief.js's colour and nothing changes, and at a grazing tilt the sliver is
   the horizon's own. ==== */
function projY(S, px, py, pz) {
  var L = S.layout, M = S.VP;
  var X = L.ox + px*L.k, Y = L.oy + py*L.k, Z = pz*L.k;
  var cy = M[1]*X + M[5]*(-Y) + M[9]*Z + M[13];
  var cw = M[3]*X + M[7]*(-Y) + M[11]*Z + M[15];
  return cw > 1e-6 ? cy/cw : NaN;
}
function skyFoot(S, D) {
  var L = S.layout;
  /* relief.js's own figure, kept as the fallback and for the pair */
  var up = (SKY_NEAR + SKY_SPAN) * D.PA * D.reach * Math.cos(S.tilt);
  var flat = Math.min(0.32, Math.max(0, 0.5 - up / Math.max(1, S.view.h)));
  if (Q.get('hz') !== 'proj') return flat;
  var RD = root.RoomDay;
  if (!RD || !RD.mapToSheet || !S.VP || !L) return flat;
  var ry = -(SKY_NEAR + SKY_SPAN) * D.PA * D.reach;      // map units, away
  var mx = S.view.x + S.view.w*0.5 + ry*Math.sin(S.yaw);
  var my = S.view.y + S.view.h*0.5 + ry*Math.cos(S.yaw);
  var q;
  try { q = RD.mapToSheet(mx, my, 0); } catch (e) { return flat; }
  if (!q) return flat;
  var px = (S.portal.l - L.ox)/L.k + (S.portal.w/L.k)*0.5;
  var py = (S.portal.t - L.oy)/L.k, ph = S.portal.h/L.k;
  var yH = projY(S, q[0], q[1], q[2]);
  var yT = projY(S, px, py, 0), yB = projY(S, px, py + ph, 0);
  if (!isFinite(yH) || !isFinite(yT) || !isFinite(yB) || Math.abs(yB - yT) < 1e-6) return flat;
  return Math.min(0.32, Math.max(0, (yH - yT) / (yB - yT)));
}

/* the foot, for a measurement (the report's number) */
W.skyFoot = function () { return G ? skyFoot(G.S, dist(G.S)) : null; };

/* ---- (4) the sky wash -------------------------------------------- */
W.drawSky = function (gl, S) {
  var deg = S.tilt / DEG;
  var k = Math.min(1, Math.max(0, (deg - SKY_FROM) / (SKY_TO - SKY_FROM)));
  if (k <= 0.002) return;
  var D = dist(S);
  if (!S.layout) return;
  var hz = skyFoot(S, D);
  var topK = Math.min(1, hz / 0.32);
  var top = [sky.hor[0] + (sky.top[0]-sky.hor[0])*topK,
             sky.hor[1] + (sky.top[1]-sky.hor[1])*topK,
             sky.hor[2] + (sky.top[2]-sky.hor[2])*topK];
  if (!P.sky && K) W.precompile(gl, S, K, true);   /* LD4: turned up before the idle slot */
  if (!live(P.sky)) return;
  gl.useProgram(P.sky);
  gl.bindVertexArray(S.vaoQuad);
  gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.depthMask(false);
  var L = S.layout;
  gl.uniform4f(P.sky.u.uPlate, (S.portal.l - L.ox)/L.k, (S.portal.t - L.oy)/L.k,
               S.portal.w/L.k, S.portal.h/L.k);
  gl.uniform3f(P.sky.u.uSheet, L.ox, L.oy, L.k);
  gl.uniformMatrix4fv(P.sky.u.uVP, false, S.VP);
  gl.uniform1f(P.sky.u.uFilm, 0.55);
  gl.uniform3f(P.sky.u.uTop, top[0], top[1], top[2]);
  gl.uniform3f(P.sky.u.uHor, sky.hor[0], sky.hor[1], sky.hor[2]);
  gl.uniform3f(P.sky.u.uHaz, sky.haz[0], sky.haz[1], sky.haz[2]);
  gl.uniform3f(P.sky.u.uKHzRain, k, hz, sky.rain);
  /* DR3: the height field behind paperSurf, written for THIS program the
     frame it is drawn — the same call engine.js:2593 makes for the ground
     and ruler.js now makes for the graduations, off the one list in
     objects.js. It returns at its first line on a build where uPapRect is
     not an active uniform, which is the stub path above. */
  if (window.RoomObjects && RoomObjects.paperInkUniforms)
    RoomObjects.paperInkUniforms(gl, P.sky, S);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.depthMask(true);
  gl.disable(gl.BLEND);
};

/* the eight waterlines — the ground's, and since R10 (a) the beyond's */
function seaUniforms(gl, p) {
  var wl = W.wl;
  if (p.u.uWL0) gl.uniform4f(p.u.uWL0, wl.lv[0], wl.lv[1], wl.lv[2], wl.lv[3]);
  if (p.u.uWL1) gl.uniform4f(p.u.uWL1, wl.lv[4], wl.lv[5], wl.lv[6], wl.lv[7]);
  if (p.u.uWLW0) gl.uniform4f(p.u.uWLW0, wl.w[0], wl.w[1], wl.w[2], wl.w[3]);
  if (p.u.uWLW1) gl.uniform4f(p.u.uWLW1, wl.w[4], wl.w[5], wl.w[6], wl.w[7]);
  if (p.u.uWLA0) gl.uniform4f(p.u.uWLA0, wl.a[0], wl.a[1], wl.a[2], wl.a[3]);
  if (p.u.uWLA1) gl.uniform4f(p.u.uWLA1, wl.a[4], wl.a[5], wl.a[6], wl.a[7]);
}

/* ---- the uniforms the ground shader's two E7 hooks want ----------- */
W.groundUniforms = function (gl, p, S) {
  var f = S.fld, D = dist(S);
  seaUniforms(gl, p);
  /* R10 (b): and the DISTANCE, for the blank paper inside the neat line —
     the conjecture recedes and Terra Incognita did not, which drew the
     unsurveyed east end of the record as a hard-edged pale RECTANGLE set
     in hazed hatch (room-rev2/s-east-t55.png). uDist.z is 0 at plan, so
     the flat sheet cannot move. */
  if (p.u.uDist) gl.uniform4f(p.u.uDist, D.PA, D.reach, D.haze, D.sinT);
  if (p.u.uHazeCol) gl.uniform3f(p.u.uHazeCol, sky.haz[0], sky.haz[1], sky.haz[2]);
  if (p.u.uPlateM) gl.uniform4f(p.u.uPlateM, recordRect(f).x, recordRect(f).y,
                                recordRect(f).w, recordRect(f).h);
  /* ==== R3 (a): the winds' centre, on the GROUND. survey-map.js:4298 —
     71 + 30/z in from the east neat line, 71 + 26/z down from the north
     one, z the fit zoom, which is 1 here because the engine's map units
     ARE the sheet's at the fit (fitWidth() is the plate's own width).

     R35: ONE ROSE, AND ITS RAYS DO NOT END. This was the BEYOND's line
     (R31) and there is one program now: 8102's reach — the sheet's
     diagonal and a fifth — put a ring round the rose out in the blank once
     the paper east of the front went on without end. The reach is no reach
     and the east run is no run; the winds thin themselves, 32 rays being
     3,900 map units apart at 20,000 out. Inside the plate neither test
     ever fired (the farthest corner is 2,068 from the rose against a reach
     of 2,627, and nothing is drawn past the east neat line at the fit), so
     the fit cannot move. The ACROSS run stays 0: uMould already carries the
     margin, and the winds die with the paper they are drawn on. ==== */
  if (p.u.uRhumb) {
    var P = f.plate;
    gl.uniform4f(p.u.uRhumb, P.x + P.w - 101, P.y + 97, 1e9, RHUMBS ? 1 : 0);
    if (p.u.uRhumbOut) gl.uniform2f(p.u.uRhumbOut, 1e9, 0);
  }
};

/* THE PLANE'S OPACITY FOR THIS FRAME (relief.js :5356 — a Fresnel-shaped
   curve on the tilt alone: .55 of the full figure at 20 degrees and below,
   all of it by 45, and NONE at plan, which is what keeps the flat sheet
   pixel for pixel). R28 hoisted it out of drawWater while measuring the
   conjecture's sea against it; the measurement is in REPORT-room-R28.md
   and the conjecture does not wear the plane, so this has one caller. */
function waterOpacity(S) {
  var shade = Math.min(1, Math.max(0, S.tilt / (15*DEG)));
  var t = Math.min(1, Math.max(0, (S.tilt - 20*DEG) / (25*DEG)));
  return WATER_OPACITY * shade * (0.55 + 0.45 * (t*t*(3-2*t)));
}

/* ---- (3) the beyond ---------------------------------------------- */
/* R35: W.drawBeyond is deleted — the ground's own pass draws it. */

/* ---- (2a) the water plane ---------------------------------------- */
W.drawWater = function (gl, S, gridRect) {
  /* relief.js :5356 — a Fresnel-shaped curve on the tilt alone: .55 of the
     full opacity at 20 degrees and below, the full figure by 45. At plan
     there is none, which is what keeps the flat swap pixel for pixel. */
  var op = waterOpacity(S);
  if (op <= 0.004) return;
  var f = S.fld, p = live(P.wat);
  if (!p) return;
  gl.useProgram(p);
  gl.bindVertexArray(S.vaoGrid);
  gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
  gl.uniform1i(p.u.uField, 0);
  G.setCommon(gl, p);
  gl.uniform4f(p.u.uGrid, gridRect.x, gridRect.y, gridRect.w, gridRect.h);
  var R2 = recordRect(f);
  gl.uniform4f(p.u.uPlateM, R2.x, R2.y, R2.w, R2.h);
  /* the block's own east limit, and the west end of the record */
  var east = f.front.none ? f.front.west : (f.front.all ? f.front.east : f.front.e1);
  gl.uniform2f(p.u.uLimA, f.front.west, east);
  gl.uniform1f(p.u.uOpac, op);
  gl.uniform1f(p.u.uFilm, 0.55);
  if (p.u.uLampS) {     /* WL1: the lantern (LB1: in the print too) */
    var lsh = (window.RoomObjects && RoomObjects.lampSheet) ? RoomObjects.lampSheet(S) : [0, 0, 1, 1, 0];
    gl.uniform4f(p.u.uLampS, lsh[0], lsh[1], lsh[2], lsh[3]);
    gl.uniform1f(p.u.uLampN, lsh[4]);
  }
  gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.depthMask(false);
  gl.drawElements(gl.TRIANGLES, S.grid.n, gl.UNSIGNED_INT, 0);
  gl.depthMask(true);
  gl.disable(gl.BLEND);
};

/* R35: W.drawSeam (R19's curtain) and W.drawFace (E7's cut face) are
   deleted — see the note at (2b). The ground draws both.  */

})(window);
